import memoize from 'lodash/memoize';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import compact from 'lodash/compact';
import isInteger from 'lodash/isInteger';
import { isNil, isObject } from 'lodash';

import { captureException } from '@jerry-serverless/next-sentry';
import type { SignupParams as SignupParamCommon } from '@jerry-serverless/types';
import { isAddressDescription } from '@jerry-serverless/enums/address_description';

import type { AppData } from './app_data';
import { parseAddress } from './address';
import { isPolicyStatus } from './policy_status.enum';
import { parseShopForInsuranceTypes } from './shop_for_insurance';
import { isNotInsuredReason } from './not_insured_reason.enum';
import { isVisitReason } from './visit_reason.enum';
import { isAutoInsuredLength } from './auto_insured_length.enum';
import { isHomeOwnershipEnumValue } from './homeownership.enum';
import { parseInitialCar } from './car';
import { parseIncome } from './driver';
import { isCreditRangeEnumValue } from './credit_range.enum';
import { isTrafficSchoolStatusEnumValue } from './traffic_school.enum';
import { isLanguageTypeEnumValue } from './language_type.enum';

export type SignupParams = SignupParamCommon & {
  /**
   * User is insured for multiple cars and drivers
   */
  multipleCarsAndDrivers?: boolean;

  /**
   * Prefill quoting ABTest sessionID and variation. Will remove after ABTest is finished
   */
  partnerSignupSessionID?: string;
  partnerSignupVariation?: number;

  // User accept insurance service in garage flow
  insuranceAccepted?: boolean;
};

export type ViolationsOption = 'Yes' | 'No' | "I don't remember";

export const parseSignupParamsNoCache = (json?: string | null, appData?: AppData): SignupParams => {
  const params: SignupParams = {
    addresses: [],
    accidents: [],
    // process.env.PERCENT_ENABLE_SHOPFOR is 1, so we set shopForEnabled to true.
    shopForEnabled: true,
    shopFor: { home: false, renters: false, condo: false, umbrella: false },
    validated: false,
    // process.env.PERCENT_ENABLE_ASKICVBEFOREONBOARDING is 1, so we set reorderSignUpEnabled to true.
    reorderSignUpEnabled: true,
    partnerSignup: false,
    askVehicleOwnership: false,
    askReferral: true, //Enabled for 100% of users
    skipSteps: [],
    violationsNotSure: false,
  };

  if (json) {
    try {
      const vals = JSON.parse(json);

      if (isArray(vals.addresses)) {
        params.addresses = compact(map(vals.addresses, (address) => parseAddress(address)));
      }

      if (isArray(vals.accidents)) {
        params.accidents = compact(map(vals.accidents, (accident) => (isString(accident) ? accident : null)));
      }

      if (isBoolean(vals.validated)) {
        params.validated = vals.validated;
      }

      if (isNumber(vals.enteredPremium)) {
        params.enteredPremium = vals.enteredPremium;
      }
      if (isNumber(vals.premiumTerm)) {
        params.premiumTerm = vals.premiumTerm;
      }

      if (isNumber(vals.enteredHomePremium)) {
        params.enteredHomePremium = vals.enteredHomePremium;
      }
      if (isNumber(vals.homePremiumTerm)) {
        params.homePremiumTerm = vals.homePremiumTerm;
      }

      // DEPRECATED: lastStep
      // DEPRECATED: furthestStep

      if (isBoolean(vals.claims)) {
        params.claims = vals.claims;
      }

      if (!isNil(vals.faultAccident)) {
        params.faultAccident = vals.faultAccident;
      }

      if (!isNil(vals.bodilyInjuryTier)) {
        params.bodilyInjuryTier = vals.bodilyInjuryTier;
      }

      if (isArray(vals.carsWithFullCoverage)) {
        params.carsWithFullCoverage = vals.carsWithFullCoverage;
      }

      if (!isNil(vals.faultAccident)) {
        params.faultAccident = vals.faultAccident;
      }

      if (isBoolean(vals.violations)) {
        params.violations = vals.violations;
      }

      if (!isNil(vals.violationsCount)) {
        params.violationsCount = vals.violationsCount;
      }

      if (isBoolean(vals.isAutoInsured)) {
        params.isAutoInsured = vals.isAutoInsured;
      }

      if (isNotInsuredReason(vals.notInsuredReason)) {
        params.notInsuredReason = vals.notInsuredReason;
      }

      if (isBoolean(vals.addressBusiness)) {
        params.addressBusiness = vals.addressBusiness;
      }

      if (isAddressDescription(vals.addressDescription)) {
        params.addressDescription = vals.addressDescription;
      }

      if (isBoolean(vals.useCustomPolicy)) {
        params.useCustomPolicy = vals.useCustomPolicy;
      }

      if (isBoolean(vals.donePolicyLoading)) {
        params.donePolicyLoading = vals.donePolicyLoading;
      }

      if (isPolicyStatus(vals.policyStatus)) {
        params.policyStatus = vals.policyStatus;
      }

      if (isString(vals.policyCarrier)) {
        params.policyCarrier = vals.policyCarrier;
      }

      if (isBoolean(vals.isAutoCarrierChanged)) {
        params.isAutoCarrierChanged = vals.isAutoCarrierChanged;
      }

      if (isBoolean(vals.isPredicted)) {
        params.isPredicted = vals.isPredicted;
      }

      if (isBoolean(vals.predictedEdited)) {
        params.predictedEdited = vals.predictedEdited;
      }

      if (isBoolean(vals.shopForEnabled)) {
        params.shopForEnabled = vals.shopForEnabled;
      }
      params.shopFor = parseShopForInsuranceTypes(vals.shopFor);

      if (isBoolean(vals.dobChanged)) {
        params.dobChanged = vals.dobChanged;
      }

      if (isBoolean(vals.addressChanged)) {
        params.addressChanged = vals.addressChanged;
      }

      if (isBoolean(vals.pf)) {
        params.pf = vals.pf;
      }

      if (isVisitReason(vals.visitReason)) {
        params.visitReason = vals.visitReason;
      }

      if (isBoolean(vals.reorderSignUpEnabled)) {
        params.reorderSignUpEnabled = vals.reorderSignUpEnabled;
      }

      if (isBoolean(vals.partnerSignup)) {
        params.partnerSignup = vals.partnerSignup;
      }

      if (isBoolean(vals.partnerSignupConfirmed)) {
        params.partnerSignupConfirmed = vals.partnerSignupConfirmed;
      }

      if (isArray(vals.skipSteps)) {
        params.skipSteps = compact(map(vals.skipSteps, (skipStep) => (isString(skipStep) ? skipStep : null)));
      }

      if (isString(vals.referralOther)) {
        params.referralOther = vals.referralOther;
      }

      if (isString(vals.irClickID)) {
        params.irClickID = vals.irClickID;
      }

      if (isAutoInsuredLength(vals.autoInsuredLength)) {
        params.autoInsuredLength = vals.autoInsuredLength;
      }
      if (isInteger(vals.continuousCoverageLength) && vals.continuousCoverageLength > 0) {
        params.continuousCoverageLength = vals.continuousCoverageLength;
      }

      if (isBoolean(vals.telematics)) {
        params.telematics = vals.telematics;
      }

      if (isBoolean(vals.askVehicleOwnership)) {
        params.askVehicleOwnership = vals.askVehicleOwnership;
      }

      if (isBoolean(vals.askReferral)) {
        params.askReferral = vals.askReferral;
      }

      if (isHomeOwnershipEnumValue(vals.homeOwnership)) {
        params.homeOwnership = vals.homeOwnership;
      }

      if (isBoolean(vals.multipleCarsAndDrivers)) {
        params.multipleCarsAndDrivers = vals.multipleCarsAndDrivers;
      }

      if (isBoolean(vals.hasCarLoan)) {
        params.hasCarLoan = vals.hasCarLoan;
      }

      if (isBoolean(vals.hasMinNumberCarLoanPayments) || vals.hasMinNumberCarLoanPayments === null) {
        params.hasMinNumberCarLoanPayments = vals.hasMinNumberCarLoanPayments;
      }

      if (isNumber(vals.estimatedMileage)) {
        params.estimatedMileage = vals.estimatedMileage;
      }

      if (isNumber(vals.carLoanBalance)) {
        params.carLoanBalance = vals.carLoanBalance;
      }

      if (isNumber(vals.carLoanMonthlyPayment)) {
        params.carLoanMonthlyPayment = vals.carLoanMonthlyPayment;
      }

      if (isCreditRangeEnumValue(vals.creditRange)) {
        params.creditRange = vals.creditRange;
      }

      if (isBoolean(vals.violationsNotSure)) {
        params.violationsNotSure = vals.violationsNotSure;
      }

      if (isBoolean(vals.insuranceService)) {
        params.insuranceService = vals.insuranceService;
      }

      if (isBoolean(vals.garageService)) {
        params.garageService = vals.garageService;
      }

      if (isBoolean(vals.dwepService)) {
        params.dwepService = vals.dwepService;
      }

      if (isBoolean(vals.refiService)) {
        params.refiService = vals.refiService;
      }

      if (isTrafficSchoolStatusEnumValue(vals.trafficSchoolStatus)) {
        params.trafficSchoolStatus = vals.trafficSchoolStatus;
      }

      if (isLanguageTypeEnumValue(vals.languageType)) {
        params.languageType = vals.languageType;
      }

      params.initialCar = parseInitialCar(vals.initialCar, appData);

      if (vals.income) {
        params.income = parseIncome(vals.income);
      }

      if (isBoolean(vals.bindingHhmEnabled)) {
        params.bindingHhmEnabled = vals.bindingHhmEnabled;
      }

      if (isBoolean(vals.isOnboardingLite)) {
        params.isOnboardingLite = vals.isOnboardingLite;
      }

      if (isBoolean(vals.needNNOInsurance)) {
        params.needNNOInsurance = vals.needNNOInsurance;
      }

      if (isBoolean(vals.hasHouseholdVehicle)) {
        params.hasHouseholdVehicle = vals.hasHouseholdVehicle;
      }

      if (isString(vals.appOS)) {
        params.appOS = vals.appOS;
      }

      if (isString(vals.partnerSignupSessionID)) {
        params.partnerSignupSessionID = vals.partnerSignupSessionID;
      }
      if (isNumber(vals.partnerSignupVariation)) {
        params.partnerSignupVariation = vals.partnerSignupVariation;
      }
      if (isString(vals.flowType)) {
        params.flowType = vals.flowType;
      }

      if (isObject(vals.nameVariation)) {
        params.nameVariation = vals.nameVariation;
      }

      params.initialOfferCheckedAt = vals.initialOfferCheckedAt;

      params.insuranceAccepted = vals.insuranceAccepted;

      params.currentCoverage = vals.currentCoverage;
      params.coveragePreference = vals.coveragePreference;
      params.coverageAdvisor = vals.coverageAdvisor;

      if (isArray(vals.customCoverages)) {
        params.customCoverages = vals.customCoverages;
      }
    } catch (err) {
      captureException(err);
    }
  }

  return params;
};

/**
 * Parse signup params JSON string.
 *
 * @param json - JSON string.
 */
export const parseSignupParams = memoize(parseSignupParamsNoCache);

/**
 * Merges new signup params object into an existing (or default) signup params object.
 *
 * @param signupParams
 * @param newSignupParams
 */
export const setSignupParams = (
  signupParams: SignupParams | null | undefined,
  newSignupParams: Partial<SignupParams>
): SignupParams => {
  const params = signupParams ? { ...signupParams } : parseSignupParams();
  return { ...params, ...newSignupParams };
};
